import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import CompanyInfoStep from './CompanyInfoStep';
import QuestionnaireStep from './QuestionnaireStep';
import ReviewStep from './ReviewStep';
import RegistrationStep from './RegistrationStep';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STEPS = [{ id: 1, label: 'Company Info' }, { id: 2, label: 'Questionnaire' }, { id: 3, label: 'Review' }, { id: 4, label: 'Register' }];

// Maps admin-selected color keyword to concrete Tailwind classes (only whitelisted values work).
export const ASSESSMENT_COLORS = {
  emerald: { bg: 'bg-emerald-600', bgHover: 'bg-emerald-700', text: 'text-emerald-600', textHover: 'text-emerald-700', ring: 'ring-emerald-500', border: 'border-emerald-500', bgSoft: 'bg-emerald-50' },
  blue:    { bg: 'bg-blue-600',    bgHover: 'bg-blue-700',    text: 'text-blue-600',    textHover: 'text-blue-700',    ring: 'ring-blue-500',    border: 'border-blue-500',    bgSoft: 'bg-blue-50' },
  purple:  { bg: 'bg-purple-600',  bgHover: 'bg-purple-700',  text: 'text-purple-600',  textHover: 'text-purple-700',  ring: 'ring-purple-500',  border: 'border-purple-500',  bgSoft: 'bg-purple-50' },
  amber:   { bg: 'bg-amber-600',   bgHover: 'bg-amber-700',   text: 'text-amber-600',   textHover: 'text-amber-700',   ring: 'ring-amber-500',   border: 'border-amber-500',   bgSoft: 'bg-amber-50' },
  rose:    { bg: 'bg-rose-600',    bgHover: 'bg-rose-700',    text: 'text-rose-600',    textHover: 'text-rose-700',    ring: 'ring-rose-500',    border: 'border-rose-500',    bgSoft: 'bg-rose-50' },
};

const AssessmentWizard = () => {
  // definitionId = admin assessment definition (e.g. ESG or DORA).
  // draftId query param = existing public_assessment id to resume.
  const { assessmentId: definitionIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const navigate = useNavigate();

  const [definitionId, setDefinitionId] = useState(definitionIdParam || null);
  const [assessmentMeta, setAssessmentMeta] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentId, setAssessmentId] = useState(null); // public_assessment id
  const [assessmentData, setAssessmentData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [resuming, setResuming] = useState(!!draftId);

  // Resume an existing draft
  useEffect(() => {
    if (!draftId) return;
    fetch(`${API_URL}/api/assessments/${draftId}`)
      .then(r => r.ok ? r.json() : null)
      .then(doc => {
        if (!doc) { setResuming(false); return; }
        setAssessmentId(doc.id);
        setDefinitionId(doc.assessment_id || definitionIdParam);
        setAssessmentData(doc);
        setCurrentStep(2); // jump straight to questionnaire
        setResuming(false);
      })
      .catch(() => setResuming(false));
  }, [draftId, definitionIdParam]);

  // If the user is logged in, fetch their profile so CompanyInfoStep can pre-fill.
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    if (!token) return;
    fetch(`${API_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(setProfile)
      .catch(() => {});
  }, []);

  // Fetch assessment definition meta (name, etc.)
  useEffect(() => {
    if (!definitionId) return;
    fetch(`${API_URL}/api/client/assessments`)
      .then(r => r.json())
      .then(list => setAssessmentMeta((list || []).find(a => a.id === definitionId) || null))
      .catch(() => {});
  }, [definitionId]);

  // Fetch questions scoped to this assessment definition
  useEffect(() => {
    if (!definitionId) return;
    fetch(`${API_URL}/api/questions?assessment_id=${definitionId}`)
      .then(r => r.json())
      .then(data => setQuestions(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, [definitionId]);

  // Resolve color palette once meta arrives
  const color = (assessmentMeta?.color && ASSESSMENT_COLORS[assessmentMeta.color]) ? assessmentMeta.color : 'emerald';
  const palette = ASSESSMENT_COLORS[color];
  const bgClass = palette.bg;
  const textClass = palette.text;

  const handleCompanyInfoSubmit = async (info) => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
      const res = await fetch(`${API_URL}/api/assessments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ company_info: info, definition_id: definitionId }),
      });
      const data = await res.json();
      setAssessmentId(data.id);
      setAssessmentData({ company_info: info });
      setCurrentStep(2);
    } catch (err) { console.error(err); }
  };

  // Logged-in users skip step 4 (Register).  After review, just submit + redirect.
  const handleSubmitForLoggedInUser = async () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
    if (!token || !assessmentId) return;
    await fetch(`${API_URL}/api/assessments/${assessmentId}/submit`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    navigate('/dashboard');
  };

  const isLoggedIn = Boolean(profile);
  // Compute steps dynamically so logged-in users see one less step.
  const stepsToShow = isLoggedIn ? STEPS.slice(0, 3) : STEPS;

  // If no assessmentId in URL, the user probably came directly to /assessment — send them home.
  if (!definitionId && !resuming) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center max-w-md">
          <h2 className="text-xl font-bold text-gray-900 mb-2">No assessment selected</h2>
          <p className="text-sm text-gray-500 mb-5">Please pick an assessment from the landing page.</p>
          <button onClick={() => navigate('/')} data-testid="back-to-landing-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors">
            Browse Assessments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4"
      style={{ '--wizard-color': assessmentMeta?.color ? `var(--tw-${assessmentMeta.color}, #10b981)` : '#10b981' }}
      data-assessment-color={assessmentMeta?.color || 'emerald'}
    >
      <div className="max-w-3xl mx-auto mb-6">
        {assessmentMeta && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Assessment</p>
              <h1 className="text-xl font-bold text-gray-900" data-testid="wizard-assessment-name">{assessmentMeta.name}</h1>
            </div>
            <button onClick={() => navigate('/')} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-full px-3 py-1.5 bg-white">
              Change
            </button>
          </div>
        )}
        <div className="flex items-center justify-between">
          {stepsToShow.map((step, i) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center gap-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${currentStep >= step.id ? `${bgClass} text-white` : 'bg-gray-200 text-gray-400'}`}>{step.id}</div>
                <span className={`text-xs font-medium ${currentStep >= step.id ? textClass : 'text-gray-400'}`}>{step.label}</span>
              </div>
              {i < stepsToShow.length - 1 && <div className={`flex-1 h-0.5 mx-2 mb-6 ${currentStep > step.id ? bgClass : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="max-w-5xl mx-auto">
        {currentStep === 1 && <CompanyInfoStep onSubmit={handleCompanyInfoSubmit} color={color}
          schema={assessmentMeta?.company_info_schema}
          profileExtras={profile?.profile_extras || {}}
          initialData={profile ? {
            company_name: profile.company_name || '',
            contact_email: profile.email || '',
            contact_phone: profile.contact_phone || '',
            primary_nace_code: profile.primary_nace_code || '',
            nace_codes: profile.nace_codes || [],
          } : undefined} />}
        {currentStep === 2 && <QuestionnaireStep assessmentId={assessmentId} definitionId={definitionId} initialAssessment={assessmentData} color={color} onNext={(data) => { setAssessmentData(data); setCurrentStep(3); }} />}
        {currentStep === 3 && <ReviewStep assessmentData={assessmentData} questions={questions} color={color}
          onNext={isLoggedIn ? handleSubmitForLoggedInUser : (data) => { setAssessmentData(data); setCurrentStep(4); }}
          submitLabel={isLoggedIn ? "Submit Assessment" : undefined} />}
        {currentStep === 4 && !isLoggedIn && <RegistrationStep assessmentId={assessmentId} assessmentData={assessmentData} color={color} />}
      </div>
    </div>
  );
};

export default AssessmentWizard;
